import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { AiUsageLog } from './AiUsageLog';
import { AiMonthlySummary } from './AiMonthlySummary';
import { HelpLink } from '../HelpLink';

const USES = [
  { key: 'chat_model', title: 'Customer chat assistant', text: 'Answers shopper questions in the website chat.' },
  { key: 'writer_model', title: 'Page builder AI writer', text: 'Drafts page copy, SEO text, alt text, section suggestions and audits.' },
  { key: 'rendering_model', title: 'AI rendering helper', text: 'Tidies your free-text room and style notes before a render. The image engine, swatch and Roman references, openness and QC rules never change.' },
];

// Lets admins pick which language model powers each AI feature (Emergent LLM key).
export const AiModelsPanel = ({ api, canEdit }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.aiModels().then(setData).catch(() => toast.error('Could not load AI model settings')); }, [api]);
  if (!data) return <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div>;
  const save = async () => {
    setSaving(true);
    try { setData(await api.saveAiModels({ chat_model: data.chat_model, writer_model: data.writer_model, rendering_model: data.rendering_model })); toast.success('AI models saved'); }
    catch { toast.error('Could not save AI models'); }
    setSaving(false);
  };
  return (
    <div className="max-w-5xl space-y-6" data-testid="ai-models-panel">
      <p className="text-sm text-neutral-600">Choose the language model for each AI feature. If the chosen model is unavailable, another one answers automatically. Usage is billed to your Universal Key balance; more advanced models cost more and respond more slowly. <HelpLink topic="ai-models" /></p>
      {USES.map((u) => (
        <div key={u.key} className="bg-white border border-neutral-200 rounded-lg p-5">
          <h3 className="font-medium text-neutral-900">{u.title}</h3>
          <p className="text-sm text-neutral-500 mb-4">{u.text}</p>
          <div className="grid sm:grid-cols-2 gap-2" role="radiogroup" aria-label={u.title}>
            {data.models.map((m) => {
              const active = data[u.key] === m.id;
              return (
                <button key={m.id} type="button" role="radio" aria-checked={active} disabled={!canEdit}
                  onClick={() => setData({ ...data, [u.key]: m.id })} data-testid={`ai-model-${u.key}-${m.id}`}
                  className={`text-left p-3 rounded border transition-colors ${active ? 'border-amber-500 bg-amber-50' : 'border-neutral-200 hover:border-neutral-400'} disabled:opacity-60`}>
                  <span className="block text-sm font-medium text-neutral-900">{m.label}</span>
                  <span className="block text-xs text-neutral-500">{m.note}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {canEdit && <Button onClick={save} disabled={saving} data-testid="ai-models-save-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save AI models</Button>}
      {data.quality_model && <p className="text-sm text-neutral-600 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="ai-quality-model-note"><b>Quality model:</b> {data.quality_model.label} is used only when you switch on “Use quality model” for a heavy action (Check page, Suggest sections, Create page with AI). It is never chosen automatically.</p>}
      {canEdit && <div className="pt-4 border-t border-neutral-200 space-y-8"><AiMonthlySummary api={api} /><AiUsageLog api={api} /></div>}
    </div>
  );
};
